import { useContext, useEffect, useRef, useState } from 'react'
import {
  getRequest,
  postRequest,
  putRequest,
  deleteRequest,
} from '../../../services/base'
import { isJson } from '../../../utils/json'
import { useParams } from 'react-router-dom'
import { NavContext } from '../../../context/global_context'
import { downloadFileBlob, formatEmptyValue } from '../../../utils/common'
import {
  Card,
  Empty,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Typography,
} from 'antd'
import Button from '../../../antd/common/Button'
import InputText from '../../../antd/common/InputText'
import TextArea from '../../../antd/common/TextArea'
import useDebouncedValue from '../../../utils/common_function'
import EllipsisTooltip from '../../../antd/common/EllipsisTooltip'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useVehicles } from '../../../context/vehicle_context'
import { useIntl } from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const DemoPayload = () => {
  const intl = useIntl()
  const { vehicle } = useParams()
  const [navState, navDispatch] = useContext(NavContext)

  const [searchValue, setSearchValue] = useState(null)
  const [data, setData] = useState([])
  const [selectedPayload, setSelectedPayload] = useState({})
  const [selectedPayloadName, setSelectedPayloadName] = useState(null)
  const [selectedPayloadId, setSelectedPayloadId] = useState(null)
  const [editingRow, setEditingRow] = useState(null)
  const [hasInitialData, setHasInitialData] = useState(false)
  const demoListUrl = `/api/vehicle_demo_json/?vehicle=${vehicle}`
  const nextUrl = useRef(demoListUrl)
  const [form] = Form.useForm()
  const debouncedSearch = useDebouncedValue(searchValue, 500)
  const [modal, contextHolder] = Modal.useModal()
  const { selectedVehicleName } = useVehicles()

  /* Load demo payloads on mount */
  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        // header: vehicle ? vehicle : 'New',
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header: `${intl.formatMessage({ id: 'layout.vehicleList' })} / ${selectedVehicleName} / ${intl.formatMessage({ id: 'demoPayload.title' })}`,
        id: '',
        show_back_btn: true,
        dtc_details: {},
        vehicle: vehicle,
        // back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
    getPayloadData()
  }, [])

  /* Debounce demo payload search */
  useEffect(() => {
    if (debouncedSearch === null || debouncedSearch === undefined) return

    // console.log('Search: ', debouncedSearch)
    nextUrl.current = `/api/vehicle_demo_json/?vehicle=${vehicle}&search=${debouncedSearch}`
    getPayloadData(true)
  }, [debouncedSearch])

  /* Pre-fill initial form values */
  useEffect(() => {
    if (selectedPayloadName || selectedPayloadName === '') {
      // console.log('Name Changed: ', selectedPayloadName)
      form.setFieldsValue({ name: selectedPayloadName })
    }
    if (selectedPayload) {
      // console.log('Payload Changed: ', selectedPayload)
      form.setFieldsValue({
        data:
          typeof selectedPayload === 'object'
            ? JSON.stringify(selectedPayload)
            : selectedPayload,
      })
    }
  }, [selectedPayloadName, selectedPayload])

  /* Demo Payload Table Data */
  const payloadData = data?.map((resRow, resIdx) => {
    return {
      key: resIdx,
      payloadName: resRow.name,
      editPayload: resRow,
      deletePayload: (
        <Button
          type="link"
          onClick={() => confirmDelete(resRow.id, resRow.name)}
          className="link-button danger"
        >
          {intl.formatMessage({ id: 'forms.delete' })}
        </Button>
      ),
    }
  })

  /* Demo Payload Table columns */
  const columns = [
    {
      title: intl.formatMessage({ id: 'demoPayload.activePayloads' }),
      className: 'payload-name',
      dataIndex: 'payloadName',
      sorter: (a, b) => a.payloadName.length - b.payloadName.length,
      ellipsis: { showTitle: false },
      align: 'left',
      render: (text) => <EllipsisTooltip className='ellipsis'>{formatEmptyValue(text)}</EllipsisTooltip>,
    },
    {
      title: '',
      className: 'edit-payload',
      dataIndex: 'editPayload',
      align: 'center',
      width: '54px',
      render: (resRow, record) => {
        const isEditing = selectedPayloadId === resRow.id
        return isEditing ? (
          <Button
            type="link"
            onClick={() => resetPayloadData(null, record.key)}
            className="link-button"
          >
            Clear
          </Button>
        ) : (
          <Button
            type="link"
            onClick={() => resetPayloadData(resRow, record.key)}
            className="link-button"
          >
            {intl.formatMessage({ id: 'forms.edit' })}
          </Button>
        )
      },
    },
    {
      title: '',
      className: 'delete-payload',
      dataIndex: 'deletePayload',
      align: 'center',
      width: '73px',
    },
  ]

  const getPayloadData = async (isSearch = false) => {
    if (nextUrl.current || isSearch) {
      let res = await getRequest(
        nextUrl.current ? nextUrl.current : demoListUrl
      )
      if (res?.status === false) {
        notifyError(navDispatch, intl.formatMessage({ id: 'demoPayload.loadError' }))
        return
      }
      nextUrl.current = res?.pagination?.nextPageLink
      if (!isSearch) {
        let tempRes = [...data]
        setData(tempRes.concat([...res?.results]))
        if (res.results.length > 0 && hasInitialData === false) {
          setHasInitialData(true)
        }
      } else {
        setData(res?.results)
      }
    }
  }

  const saveDemoPayload = async (values) => {
    const { name, data, vehicle } = values
    const payload = typeof data === 'object' ? JSON.stringify(data) : data
    const validJSON = isJson(payload)

    /* Invalid JSON */
    if (!validJSON) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'demoPayload.invalidJson' }),
        },
      })

      return
    }

    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'demoPayload.saving' }),
        showLoader: true,
      },
      notification: {},
    })

    try {
      const payloadData = JSON.parse(payload)
      const formData = new FormData()
      formData.append('name', name)
      formData.append('data', JSON.stringify(payloadData))
      formData.append('vehicle', vehicle)

      let res
      if (selectedPayloadId) {
        res = await putRequest(
          `/api/vehicle_demo_json/${selectedPayloadId}/?vehicle=${vehicle}`,
          formData
        )
      } else {
        res = await postRequest(`/api/vehicle_demo_json/`, formData)
      }

      nextUrl.current = `/api/vehicle_demo_json/?vehicle=${vehicle}&search=${searchValue || ''}`

      if (res?.status) {
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'success',
            msg: intl.formatMessage({ id: 'demoPayload.saveSuccess' }),
          },
        })

        resetPayloadData()
        getPayloadData(true)
      } else {
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'error',
            msg: intl.formatMessage({ id: 'demoPayload.error' }),
          },
        })
      }
    } catch (err) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'demoPayload.invalidJsonFormat' }),
        },
      })
    }
  }

  const deleteSelectedPayload = async (id, name) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'demoPayload.deleting' }, { name }),
        showLoader: true,
      },
      notification: {},
    })

    let res = await deleteRequest(
      `/api/vehicle_demo_json/${id}/?vehicle=${vehicle}`
    )

    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'demoPayload.deleteSuccess' }),
        },
      })

      nextUrl.current = `/api/vehicle_demo_json/?vehicle=${vehicle}&search=${searchValue || ''}`
      resetPayloadData()
      getPayloadData(true)
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'demoPayload.error' }),
        },
      })
    }
  }

  const resetPayloadData = (resRow, recordKey) => {
    // console.log(recordKey, resRow)
    setEditingRow(resRow ? recordKey : null)
    setSelectedPayloadId(resRow ? resRow.id : null)
    setSelectedPayload(resRow ? resRow?.data : {})
    setSelectedPayloadName(resRow ? resRow.name : '')
  }

  const downloadZip = async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'demoPayload.downloading' }),
        showLoader: true,
      },
      notification: {},
    })

    let res = await postRequest(
      `/api/vehicle_demo_json/download/${vehicle}/`,
      true,
      true
    )
    // Was `if (res?.msg)`: a failure carrying no message fell through and saved
    // the error object as the .zip.
    if (res?.status === false || !(res instanceof Blob)) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'demoPayload.downloadError' }),
        },
      })
    } else {
      downloadFileBlob(new Blob([res]), `${vehicle}-demo.zip`)
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'demoPayload.downloadSuccess' }),
        },
      })
    }
  }

  const confirmSave = () => {
    modal.confirm({
      title: intl.formatMessage({ id: 'forms.confirm' }),
      icon: <ExclamationCircleOutlined />,
      content: `${selectedPayloadId ? intl.formatMessage({ id: 'demoPayload.confirmSave' }) + ' ' : ''} Are you sure want to save`,
      onOk: async () => {
        try {
          const values = await form.validateFields()
          await saveDemoPayload(values)
        } catch (err) {
          console.log(err)
        }
      },
      centered: true,
      maskClosable: true,
      width: '40%',
    })
  }

  const confirmDelete = (id, name) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'forms.confirm' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({ id: 'demoPayload.confirmDelete' }, { name }),
      onOk: () => {
        deleteSelectedPayload(id, name)
      },
      centered: true,
      maskClosable: true,
      width: '40%',
    })
  }

  return (
    <div className="page vehicle-demo-payload">
      <Row className="title">
        <Space size={'middle'}>
          <Typography.Title level={5}>{intl.formatMessage({ id: 'demoPayload.title' })}</Typography.Title>
        </Space>
        {data?.length > 0 && (
          <Space size={'middle'}>
            <Button onClick={(e) => downloadZip()}>{intl.formatMessage({ id: 'forms.download' })}</Button>
          </Space>
        )}
      </Row>
      <Row className="body">
        <div className="vehicle-form demo-payload-wrapper">
          <Card title="" variant="outlined">
            <Flex gap={24}>
              <Form
                id="demo-payload"
                name="demo-payload"
                form={form}
                layout="vertical"
                onFinish={confirmSave}
                style={{ width: '50%' }}
              >
                <Form.Item name="vehicle" initialValue={vehicle} hidden>
                  <Input />
                </Form.Item>

                <Flex vertical gap={16} style={{ paddingBottom: 16 }}>
                  <InputText
                    name="name"
                    label={intl.formatMessage({ id: 'demoPayload.payloadName' })}
                    placeholder={intl.formatMessage({ id: 'demoPayload.payloadNamePlaceholder' })}
                    initialValue={selectedPayloadName}
                    type="text"
                    onChange={(value) => {
                      setSelectedPayloadName(value)
                    }}
                    isRequired={true}
                    key={`name-${selectedPayloadId}`}
                  />

                  <TextArea
                    name="data"
                    value={
                      typeof selectedPayload === 'object'
                        ? JSON.stringify(selectedPayload)
                        : selectedPayload
                    }
                    onChange={(value) => {
                      setSelectedPayload(value)
                    }}
                    isRequired={true}
                    placeholder={intl.formatMessage({ id: 'demoPayload.payloadJson' })}
                    key={`data-${selectedPayloadId}`}
                    label={intl.formatMessage({ id: 'demoPayload.payloadJson' })}
                    rows={20}
                  />
                </Flex>
                <Button htmlType="submit" type="primary">
                  {intl.formatMessage({ id: 'demoPayload.savePayload' })}
                </Button>
              </Form>
              <div style={{ width: '50%' }}>
                <Form layout="vertical" className="demo-payload-search">
                  <Flex vertical gap={16}>
                    {hasInitialData && (
                      <InputText
                        name="search_payload"
                        label={intl.formatMessage({ id: 'demoPayload.searchPayload' })}
                        placeholder={intl.formatMessage({ id: 'demoPayload.searchPlaceholder' })}
                        value={searchValue}
                        type="search"
                        isRequired={false}
                        onChange={(value) => {
                          setSearchValue(value)
                        }}
                      />
                    )}

                    <Row className="table-container">
                      <Table
                        tableLayout="fixed"
                        dataSource={payloadData}
                        columns={columns}
                        showSorterTooltip={false}
                        rowClassName={(record) =>
                          editingRow == record.key ? 'editing-row' : ''
                        }
                        loading={!data ? true : false}
                        pagination={false}
                        scroll={{ y: 444 }}
                        locale={{
                          emptyText: (
                            <Empty
                              description={
                                searchValue
                                  ? 'No search results found'
                                  : 'No records found'
                              }
                            />
                          ),
                        }}
                      />
                    </Row>
                    {nextUrl.current && (
                      <Button
                        type="link"
                        onClick={() => getPayloadData()}
                        className="link-button underline"
                      >
                        {intl.formatMessage({ id: 'forms.loadMore' })}
                      </Button>
                    )}
                  </Flex>
                </Form>
              </div>
            </Flex>
          </Card>
        </div>
      </Row>
      {contextHolder}
    </div>
  )
}

export default DemoPayload
